import React, { useEffect, useState } from "react";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ImageUpload";
import { PLATFORM_META } from "@/components/SocialIcons";
import { LocationSelect } from "@/components/LocationSelect";
import { AccountControls } from "@/components/AccountControls";
import { ConnectedSocials } from "@/components/ConnectedSocials";
import { TermsAcceptedNote } from "@/components/TermsAcceptedNote";
import { MediaKitProCard } from "@/components/mediakit/MediaKitProCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Link2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { EmailNotificationToggle } from "@/components/EmailNotificationToggle";

export default function CreatorEdit() {
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [p, setP] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/meta"), api.get("/creator/profile/me")]).then(([m, prof]) => {
      setMeta(m.data);
      const d = prof.data;
      setP({
        full_name: d.full_name || "", display_name: d.display_name || "", location: d.location || "",
        areas_covered: d.areas_covered || [], bio: d.bio || "", niches: d.niches || [],
        profile_photo: d.profile_photo || "",
        socials: {
          instagram: d.socials?.instagram || { username: "", url: "", followers: "", avg_views: "", engagement: "" },
          tiktok: d.socials?.tiktok || { username: "", url: "", followers: "", avg_views: "", engagement: "" },
          youtube: d.socials?.youtube || { username: "", url: "", followers: "", avg_views: "", engagement: "" },
        },
        portfolio: d.portfolio || [],
        services: d.services || [],
        gifted_ok: d.gifted_ok || false,
      });
    }).finally(() => setLoading(false));
  }, []);

  const toggle = (field, val) => setP((s) => ({
    ...s, [field]: s[field].includes(val) ? s[field].filter((x) => x !== val) : [...s[field], val],
  }));

  const setSocial = (plat, key, val) => setP((s) => ({
    ...s, socials: { ...s.socials, [plat]: { ...s.socials[plat], [key]: val } },
  }));

  const addPortfolio = (item) => setP((s) => ({ ...s, portfolio: [...s.portfolio, { id: crypto.randomUUID(), ...item }] }));
  const removePortfolio = (id) => setP((s) => ({ ...s, portfolio: s.portfolio.filter((x) => x.id !== id) }));

  const toggleService = (type) => setP((s) => {
    const exists = s.services.find((x) => x.type === type);
    return { ...s, services: exists ? s.services.filter((x) => x.type !== type) : [...s.services, { type, price_from: "" }] };
  });
  const setServicePrice = (type, price) => setP((s) => ({
    ...s, services: s.services.map((x) => (x.type === type ? { ...x, price_from: price } : x)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...p,
        socials: Object.fromEntries(Object.entries(p.socials).map(([k, v]) => [k, {
          username: v.username || "", url: v.url || "",
          followers: Number(v.followers) || 0, avg_views: Number(v.avg_views) || 0, engagement: Number(v.engagement) || 0,
        }])),
        services: p.services.map((s) => ({ type: s.type, price_from: s.price_from === "" ? null : Number(s.price_from) })),
      };
      await api.put("/creator/profile", payload);
      toast.success("Profile saved");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !p) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  const card = "rounded-3xl border border-zinc-200 bg-white p-6";

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-action">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Your creator profile</h1>
          <Button data-testid="creator-save-btn" onClick={save} disabled={saving} className="hidden rounded-full bg-emerald-600 hover:bg-emerald-700 md:inline-flex">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Basics */}
          <div className={card}>
            <h2 className="font-display text-lg font-bold text-zinc-900">Basics</h2>
            <div className="mt-4"><Label>Profile photo</Label><div className="mt-2"><ImageUpload value={p.profile_photo} onChange={(v) => setP({ ...p, profile_photo: v })} testid="creator-photo-upload" round /></div></div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><Label>Full name</Label><Input data-testid="creator-fullname" value={p.full_name} onChange={(e) => setP({ ...p, full_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Display name</Label><Input data-testid="creator-displayname" value={p.display_name} onChange={(e) => setP({ ...p, display_name: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="mt-4">
              <Label>Location (your base)</Label>
              <LocationSelect testid="creator-location" value={p.location} onChange={(v) => setP({ ...p, location: v })}
                groups={meta.location_groups || []} placeholder="Select your base" />
            </div>
            <div className="mt-4">
              <Label>Areas you cover</Label>
              <div className="mt-2 max-h-64 space-y-3 overflow-y-auto rounded-xl border border-zinc-100 p-3">
                {(meta.location_groups || []).map((g) => (
                  <div key={g.region}>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-700">{g.region}</p>
                    <div className="flex flex-wrap gap-2">
                      {g.locations.map((l) => (
                        <button key={l} type="button" data-testid={`creator-area-${l}`} onClick={() => toggle("areas_covered", l)}
                          className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors ${p.areas_covered.includes(l) ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4"><Label>Short bio</Label><Textarea data-testid="creator-bio" rows={3} value={p.bio} onChange={(e) => setP({ ...p, bio: e.target.value })} className="mt-1.5" placeholder="Tell businesses about your content and audience…" /></div>
          </div>

          {/* Niches */}
          <div className={card}>
            <h2 className="font-display text-lg font-bold text-zinc-900">Content niches</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {meta.niches?.map((n) => (
                <button key={n} type="button" data-testid={`creator-niche-${n}`} onClick={() => toggle("niches", n)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${p.niches.includes(n) ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600"}`}>{n}</button>
              ))}
            </div>
          </div>

          {/* Socials */}
          <div className={card}>
            <h2 className="font-display text-lg font-bold text-zinc-900">Social platforms</h2>
            <p className="mt-1 text-sm text-zinc-500">Add your accounts and audience stats. These are <span className="font-medium text-zinc-600">self-reported</span> — connecting an account later replaces them with verified data.</p>
            <div className="mt-4 space-y-5">
              {(meta.platforms || []).map((plat) => {
                const pm = PLATFORM_META[plat];
                if (!pm) return null;
                const { Icon, label, color } = pm;
                const acc = p.socials[plat] || { username: "", url: "", followers: "", avg_views: "", engagement: "" };
                const connected = (acc.source === "connected");
                return (
                  <div key={plat} className="rounded-2xl border border-zinc-200 p-4">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-5 w-5 ${color}`} /><span className="font-semibold text-zinc-900">{label}</span>
                      <span data-testid={`social-${plat}-source`} className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${connected ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                        {connected ? "Connected · Verified" : "Self-reported"}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <Input data-testid={`social-${plat}-username`} placeholder="Username" value={acc.username} onChange={(e) => setSocial(plat, "username", e.target.value)} />
                      <Input data-testid={`social-${plat}-url`} placeholder="Profile link" value={acc.url} onChange={(e) => setSocial(plat, "url", e.target.value)} />
                      <Input data-testid={`social-${plat}-followers`} type="number" placeholder="Followers" value={acc.followers} onChange={(e) => setSocial(plat, "followers", e.target.value)} />
                      <Input data-testid={`social-${plat}-views`} type="number" placeholder="Avg views" value={acc.avg_views} onChange={(e) => setSocial(plat, "avg_views", e.target.value)} />
                      <Input data-testid={`social-${plat}-engagement`} type="number" step="0.1" placeholder="Engagement %" value={acc.engagement} onChange={(e) => setSocial(plat, "engagement", e.target.value)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Portfolio */}
          <PortfolioEditor portfolio={p.portfolio} addPortfolio={addPortfolio} removePortfolio={removePortfolio} />

          {/* Services */}
          <div className={card}>
            <h2 className="font-display text-lg font-bold text-zinc-900">Collaboration options &amp; pricing</h2>
            <p className="mt-1 text-sm text-zinc-500">Select what you offer and a starting price.</p>
            <div className="mt-4 space-y-2">
              {meta.services?.map((type) => {
                const selected = p.services.find((x) => x.type === type);
                return (
                  <div key={type} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${selected ? "border-emerald-300 bg-emerald-50/50" : "border-zinc-200"}`}>
                    <Switch data-testid={`service-toggle-${type}`} checked={!!selected} onCheckedChange={() => toggleService(type)} />
                    <span className="flex-1 text-sm font-medium text-zinc-700">{type}</span>
                    {selected && (
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-zinc-500">From £</span>
                        <Input data-testid={`service-price-${type}`} type="number" value={selected.price_from} onChange={(e) => setServicePrice(type, e.target.value)} className="h-9 w-24" placeholder="0" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-5 flex items-center justify-between rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
              <div><p className="font-medium text-indigo-900">Open to gifted collaborations</p><p className="text-xs text-indigo-600">Accept products, meals or experiences instead of payment</p></div>
              <Switch data-testid="creator-gifted-toggle" checked={p.gifted_ok} onCheckedChange={(v) => setP({ ...p, gifted_ok: v })} />
            </div>
          </div>
        </div>

        <div className="mt-8"><ConnectedSocials onUsePhoto={(url) => setP((s) => ({ ...s, profile_photo: url }))} /></div>
        <div className="mt-8"><MediaKitProCard variant="profile" /></div>
        <TermsAcceptedNote />
        <div className="mt-8"><AccountControls /></div>
      </div>

      <EmailNotificationToggle />

      <div className="nf-actionbar-abovenav border-t border-zinc-200 bg-white p-3 md:hidden">
        <Button data-testid="creator-save-btn-mobile" onClick={save} disabled={saving} className="h-12 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save profile"}
        </Button>
      </div>
      <MobileNav />
    </div>
  );
}

function PortfolioEditor({ portfolio, addPortfolio, removePortfolio }) {
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPlat, setLinkPlat] = useState("instagram");
  const [linkCaption, setLinkCaption] = useState("");

  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-6">
      <h2 className="font-display text-lg font-bold text-zinc-900">Portfolio / recent content</h2>
      <p className="mt-1 text-sm text-zinc-500">Upload your best images/videos or add links to posts.</p>

      {portfolio.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
          {portfolio.map((item) => (
            <div key={item.id} className="group relative aspect-square overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100">
              {item.type === "video" ? (
                <video src={fileUrl(item.url)} className="h-full w-full object-cover" muted />
              ) : item.type === "link" && !item.thumbnail ? (
                <div className="flex h-full w-full flex-col items-center justify-center p-2 text-center"><Link2 className="h-5 w-5 text-zinc-400" /><span className="mt-1 text-[10px] text-zinc-500 line-clamp-2">{item.caption || item.platform}</span></div>
              ) : (
                <img src={fileUrl(item.thumbnail || item.url)} alt="" className="h-full w-full object-cover" />
              )}
              <button type="button" data-testid={`portfolio-remove-${item.id}`} onClick={() => removePortfolio(item.id)} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 space-y-4">
        <div className="rounded-2xl border border-dashed border-zinc-300 p-4">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-zinc-700"><ImageIcon className="h-4 w-4" /> Upload image or video</p>
          <ImageUpload value="" testid="portfolio-upload" accept="image/*,video/*" label="Choose file"
            onChange={(url) => { if (url) { const isVid = /\.(mp4|mov|webm)(\?|$)/i.test(url); addPortfolio({ type: isVid ? "video" : "image", url, thumbnail: "", platform: "", caption: "" }); } }} />
        </div>

        <div className="rounded-2xl border border-dashed border-zinc-300 p-4">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-zinc-700"><Link2 className="h-4 w-4" /> Add a post link</p>
          <div className="grid gap-2 sm:grid-cols-[1fr_140px]">
            <Input data-testid="portfolio-link-url" placeholder="https://instagram.com/p/…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
            <Select value={linkPlat} onValueChange={setLinkPlat}>
              <SelectTrigger data-testid="portfolio-link-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input data-testid="portfolio-link-caption" placeholder="Caption (optional)" value={linkCaption} onChange={(e) => setLinkCaption(e.target.value)} className="mt-2" />
          <Button type="button" variant="outline" data-testid="portfolio-add-link" className="mt-2 rounded-full"
            onClick={() => { if (linkUrl) { addPortfolio({ type: "link", url: linkUrl, thumbnail: "", platform: linkPlat, caption: linkCaption }); setLinkUrl(""); setLinkCaption(""); } }}>
            <Plus className="mr-1 h-4 w-4" /> Add link
          </Button>
        </div>
      </div>
    </div>
  );
}
